import React from 'react';
import { MdTrendingUp, MdTrendingDown, MdRemove, MdAccessTime } from 'react-icons/md';

// Mirror of backend apps.technical_signals.analytics.consolidator.INDICATOR_CATEGORIES.
// Kept client-side so we can derive momentum + mean-rev sub-scores from the
// votes dict already in the payload — no extra API call needed.
const INDICATOR_CATEGORIES = {
  sma_cross: 'momentum',
  price_vs_sma200: 'momentum',
  ema_cross: 'momentum',
  macd: 'momentum',
  adx: 'momentum',
  obv: 'momentum',
  rsi: 'mean_reversion',
  bollinger: 'mean_reversion',
  stochastic: 'mean_reversion',
  williams_r: 'mean_reversion',
  fibonacci: 'mean_reversion',
};

const scoreFromVotes = (votes, category) => {
  const relevant = Object.entries(votes || {})
    .filter(([k]) => INDICATOR_CATEGORIES[k] === category)
    .map(([, v]) => v);
  if (relevant.length === 0) return null;
  return relevant.reduce((a, b) => a + b, 0) / relevant.length;
};

const scoreToLabel = (score) => {
  if (score == null) return { label: 'Neutral', tone: 'gray' };
  if (score >= 0.40) return { label: 'Buy', tone: 'green' };
  if (score >= 0.15) return { label: 'Lean Buy', tone: 'green' };
  if (score > -0.15) return { label: 'Neutral', tone: 'gray' };
  if (score > -0.40) return { label: 'Lean Sell', tone: 'red' };
  return { label: 'Sell', tone: 'red' };
};

/**
 * Auto-generated plain-language summary of a symbol's signal stack.
 * Designed to be screenshot-friendly — anyone can read it and get the
 * gist without knowing what RSI or MACD mean.
 */

const SIGNAL_HEADLINE = {
  BUY:        { verdict: 'Strong Buy',  tone: 'green', text: 'Most indicators point up — both trend and momentum agree.' },
  WEAK_BUY:   { verdict: 'Lean Buy',    tone: 'green', text: 'More positives than negatives, but not a clean breakout.' },
  HOLD:       { verdict: 'Neutral',     tone: 'gray',  text: 'Bulls and bears are split — no clear edge right now.' },
  WEAK_SELL:  { verdict: 'Lean Sell',   tone: 'red',   text: 'More negatives than positives — caution warranted.' },
  SELL:       { verdict: 'Strong Sell', tone: 'red',   text: 'Most indicators point down — both trend and momentum weak.' },
};

const VOTE_PHRASES = {
  sma_cross: {
    pos: 'long-term trend is up (50-day above 200-day average)',
    neg: 'long-term trend is down (death cross — 50-day below 200-day)',
  },
  price_vs_sma200: {
    pos: 'price is above its 200-day average — bull-market regime',
    neg: 'price is below its 200-day average — bear-market regime',
  },
  ema_cross: {
    pos: 'short-term trend is up (12-day above 26-day average)',
    neg: 'short-term trend is down (12-day below 26-day average)',
  },
  macd: {
    pos: 'momentum is accelerating up (MACD bullish and rising)',
    neg: 'momentum is accelerating down (MACD bearish and falling)',
  },
  rsi: {
    pos: 'looks oversold — could be due for a bounce (RSI under 30)',
    neg: 'looks overbought — could be due to pull back (RSI over 70)',
  },
  bollinger: {
    pos: 'price punched below the lower volatility band — extreme low',
    neg: 'price punched above the upper volatility band — extreme high',
  },
  stochastic: {
    pos: 'short-term momentum just turned up from oversold levels',
    neg: 'short-term momentum just turned down from overbought levels',
  },
  williams_r: {
    pos: 'short-term oversold (Williams %R)',
    neg: 'short-term overbought (Williams %R)',
  },
  adx: {
    pos: 'a real uptrend is in place (ADX > 25 with bulls in control)',
    neg: 'a real downtrend is in place (ADX > 25 with bears in control)',
  },
  obv: {
    pos: 'volume is confirming the price move — buyers showing up',
    neg: 'volume is confirming the price decline — sellers showing up',
  },
  volume: {
    pos: 'a high-volume surge accompanied today\'s up-move',
    neg: 'a high-volume sell-off hit today',
  },
  fibonacci: {
    pos: 'price is sitting on a deep Fibonacci retracement — bounce zone',
    neg: 'price is being rejected at a shallow Fibonacci level — fade zone',
  },
  pivot: {
    pos: 'trading above the daily floor pivot — short-term bullish',
    neg: 'trading below the daily floor pivot — short-term bearish',
  },
  sr: {
    pos: 'sitting on a tested support level — bounce candidate',
    neg: 'pressing against a tested resistance level — rejection candidate',
  },
};

const buildBullets = (votes) => {
  const bullish = [];
  const bearish = [];
  Object.entries(votes || {}).forEach(([key, vote]) => {
    const phrases = VOTE_PHRASES[key];
    if (!phrases) return;
    if (vote > 0) bullish.push(phrases.pos);
    if (vote < 0) bearish.push(phrases.neg);
  });
  return { bullish, bearish };
};

const PlainEnglishSummary = ({ history }) => {
  if (!history) return null;
  const verdict = SIGNAL_HEADLINE[history.signal] || SIGNAL_HEADLINE.HOLD;
  const ind = history.indicators_latest || {};
  const { bullish, bearish } = buildBullets(history.votes);

  const numBullish = bullish.length;
  const numBearish = bearish.length;
  const numNeutral = (Object.values(history.votes || {}).filter(v => v === 0)).length;
  const total = numBullish + numBearish + numNeutral || 1;

  // Headline bar widths (for the simple bullish/bearish ratio bar)
  const bullPct = (numBullish / total) * 100;
  const bearPct = (numBearish / total) * 100;
  const neuPct = 100 - bullPct - bearPct;

  const accent = verdict.tone === 'green'
    ? 'border-green-500'
    : verdict.tone === 'red'
    ? 'border-red-500'
    : 'border-gray-400';
  const accentText = verdict.tone === 'green'
    ? 'text-green-600 dark:text-green-400'
    : verdict.tone === 'red'
    ? 'text-red-500 dark:text-red-400'
    : 'text-gray-500 dark:text-gray-400';

  const Icon = verdict.tone === 'green' ? MdTrendingUp : verdict.tone === 'red' ? MdTrendingDown : MdRemove;

  // Sub-bucket scores: derived client-side from votes so the detail page
  // mirrors what Best Picks shows (Momentum Buy / Mean-Rev Buy etc.).
  const momentumScore = scoreFromVotes(history.votes, 'momentum');
  const meanRevScore = scoreFromVotes(history.votes, 'mean_reversion');
  const compositeScore = Number(history.score ?? 0);
  const fmtScore = (v) => (v == null ? '—' : `${v >= 0 ? '+' : ''}${Number(v).toFixed(2)}`);
  const toneClass = (tone) => tone === 'green'
    ? 'text-green-600 dark:text-green-400'
    : tone === 'red'
    ? 'text-red-500 dark:text-red-400'
    : 'text-gray-500 dark:text-gray-400';
  const momentumVerdict = scoreToLabel(momentumScore);
  const meanRevVerdict = scoreToLabel(meanRevScore);
  const compositeVerdict = scoreToLabel(compositeScore);

  return (
    <div className={`bg-white dark:bg-[#161b22] border-l-4 ${accent} border-y border-r border-gray-200 dark:border-gray-800 rounded-lg p-3 md:p-5 mb-4`}>
      <div className="flex items-start gap-3 md:gap-4">
        <div className={`text-2xl md:text-3xl ${accentText} shrink-0 mt-1`}>
          <Icon />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap mb-1">
            <span className={`font-serif text-xl ${accentText}`}>
              {verdict.verdict}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              · {numBullish} bullish, {numBearish} bearish, {numNeutral} neutral
            </span>
          </div>
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
            {verdict.text}
          </p>

          {/* Visual bullish/bearish ratio bar */}
          <div className="mt-3 flex h-2 rounded overflow-hidden bg-gray-100 dark:bg-gray-800">
            {bullPct > 0 && <div className="bg-green-500" style={{ width: `${bullPct}%` }} title={`${numBullish} bullish`} />}
            {neuPct > 0 && <div className="bg-gray-400" style={{ width: `${neuPct}%` }} title={`${numNeutral} neutral`} />}
            {bearPct > 0 && <div className="bg-red-500" style={{ width: `${bearPct}%` }} title={`${numBearish} bearish`} />}
          </div>

          {/* Three signal lenses: composite (all 14) | momentum (6) | mean-rev (5).
              Same labels Best Picks uses, so clicking through stays consistent. */}
          <div className="grid grid-cols-3 gap-2 mt-4 pt-3 border-t border-gray-100 dark:border-gray-800">
            <div>
              <div className="text-[9px] uppercase tracking-[2px] text-gray-400 mb-0.5">Composite</div>
              <div className={`text-sm font-semibold ${toneClass(compositeVerdict.tone)}`}>
                {compositeVerdict.label}
              </div>
              <div className="text-[10px] text-gray-500 dark:text-gray-400">{fmtScore(compositeScore)}</div>
            </div>
            <div>
              <div className="text-[9px] uppercase tracking-[2px] text-gray-400 mb-0.5">Momentum</div>
              <div className={`text-sm font-semibold ${toneClass(momentumVerdict.tone)}`}>
                {momentumVerdict.label}
              </div>
              <div className="text-[10px] text-gray-500 dark:text-gray-400">{fmtScore(momentumScore)}</div>
            </div>
            <div>
              <div className="text-[9px] uppercase tracking-[2px] text-gray-400 mb-0.5">Mean-Rev</div>
              <div className={`text-sm font-semibold ${toneClass(meanRevVerdict.tone)}`}>
                {meanRevVerdict.label}
              </div>
              <div className="text-[10px] text-gray-500 dark:text-gray-400">{fmtScore(meanRevScore)}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {bullish.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[2px] text-green-600 dark:text-green-400 mb-1.5 font-semibold">
                  Bullish factors ({bullish.length})
                </div>
                <ul className="space-y-1 text-xs text-gray-700 dark:text-gray-300">
                  {bullish.map((b, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="text-green-500 shrink-0">✓</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {bearish.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[2px] text-red-500 dark:text-red-400 mb-1.5 font-semibold">
                  Bearish factors ({bearish.length})
                </div>
                <ul className="space-y-1 text-xs text-gray-700 dark:text-gray-300">
                  {bearish.map((b, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="text-red-500 shrink-0">✗</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {bullish.length === 0 && bearish.length === 0 && (
              <div className="text-xs text-gray-500 dark:text-gray-400">
                No strong factors firing right now — the market is in a quiet, balanced state for this symbol.
              </div>
            )}
          </div>

          {/* Key reference levels */}
          {(ind.fibonacci?.swing_high || ind.support_resistance) && (
            <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-800 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              {ind.fibonacci?.swing_high && (
                <KeyLevel label="52w High" value={ind.fibonacci.swing_high} close={ind.close} />
              )}
              {ind.fibonacci?.swing_low && (
                <KeyLevel label="52w Low" value={ind.fibonacci.swing_low} close={ind.close} />
              )}
              {ind.support_resistance?.resistance?.[0]?.level && (
                <KeyLevel label="Next Resistance" value={ind.support_resistance.resistance[0].level} close={ind.close} accent="red" />
              )}
              {ind.support_resistance?.support?.[0]?.level && (
                <KeyLevel label="Next Support" value={ind.support_resistance.support[0].level} close={ind.close} accent="green" />
              )}
            </div>
          )}

          <div className="mt-3 flex items-center gap-1 text-[10px] text-gray-400 dark:text-gray-600">
            <MdAccessTime /> As of {history.as_of_date} · auto-generated from {Object.keys(history.votes || {}).length} indicators · not investment advice
          </div>
        </div>
      </div>
    </div>
  );
};

const KeyLevel = ({ label, value, close, accent }) => {
  const distPct = close && value ? ((value - close) / close * 100) : null;
  const distColor = accent === 'red'
    ? 'text-red-500 dark:text-red-400'
    : accent === 'green'
    ? 'text-green-600 dark:text-green-400'
    : 'text-gray-500 dark:text-gray-400';
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[2px] text-gray-400">{label}</div>
      <div className="text-sm text-gray-900 dark:text-gray-100 font-semibold">{Number(value).toFixed(2)}</div>
      {distPct != null && (
        <div className={`text-[10px] ${distColor}`}>
          {distPct >= 0 ? '+' : ''}{distPct.toFixed(1)}% away
        </div>
      )}
    </div>
  );
};

export default PlainEnglishSummary;
